// Титульный лист по форме Прил. Б (как шаблон writer/templates/base.docx): HTML-макет с правкой полей прямо
// на листе, подсветка незаполненного и точный предпросмотр через сервер (Word -> PDF -> PNG).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Ctx, DialogProps } from '../../core/registry';
import type { Meta } from '../../core/types';
import { useApp } from '../../core/store';
import { Btn, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { wt, shortName, NB } from './data';
import { NBSP_CHAR } from '../../core/schema/constants';

/** пустая ячейка / пробел, который не должен схлопнуться в разметке листа (только показ, в DOCX не попадает) */
const UI_SP = NBSP_CHAR;
import { getProfileCached, loadProfile } from './profile';

/** Поле, редактируемое прямо на листе: однострочное - input по ширине текста, тема - textarea с авто-высотой. */
function Inline({ value, onChange, placeholder, testid, className, multiline }: {
  value: string; onChange?: (v: string) => void; placeholder: string; testid?: string; className?: string; multiline?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = '0px'; el.style.height = el.scrollHeight + 'px'; }
  }, [v]);
  if (!onChange) return <span className={'ts-val ' + (className || '') + (value ? '' : ' ts-empty')}>{value || placeholder}</span>;
  const commit = () => { if (v.trim() !== value) onChange(v.trim()); };
  const key = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLElement).blur(); }
    if (e.key === 'Escape') { setV(value); (e.target as HTMLElement).blur(); }
  };
  const cls = 'ts-input ' + (className || '') + (v ? '' : ' ts-empty');
  if (multiline) {
    return <textarea ref={ref} rows={1} className={cls} value={v} placeholder={placeholder} spellCheck lang="ru" data-testid={testid}
      onClick={(e) => e.stopPropagation()} onChange={(e) => setV(e.target.value.replace(/\n/g, ' '))} onBlur={commit} onKeyDown={key} />;
  }
  return <input className={cls} value={v} placeholder={placeholder} spellCheck lang="ru" data-testid={testid}
    size={Math.max(2, (v || placeholder).length + 1)} onClick={(e) => e.stopPropagation()}
    onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={key} />;
}

export interface TitleEdit { set: (patch: Partial<Meta>) => void }

/** Макет титульного листа - как таблица титула шаблона writer (writer/templates/base.docx, builder.fill_title):
 *  те же кегли (10/11/12/15/8 пт), одинарный интервал, графы таблицы 723/1955/1387/2078/429/2342/725 twips,
 *  строки-промежутки точной высоты 600/600/1120/680/3400 twips, линии под полями, «Вариант №» только если он задан.
 *  Поля правятся на месте (edit), остальное - щелчком открывает «Данные работы». */
export function TitleView({ m, edit, onOpen, compact }: { m: Meta; edit?: TitleEdit; onOpen?: (tab: string) => void; compact?: boolean }) {
  const w = wt(m.type);
  const st = m.student;
  // соавторы групповой работы - как writer fill_title: каждый своей строкой под первым обучающимся
  const coauthors = String(st.coauthors || '').split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
  const t = m.teacher;
  const set = edit?.set;
  const practice = m.type === 'practice';
  const [addVariant, setAddVariant] = useState(false);
  const open = (tab: string) => (e: React.MouseEvent) => { if (onOpen) { e.stopPropagation(); onOpen(tab); } };
  const showVariant = !practice && (!!m.variant || addVariant);
  const [addNumber, setAddNumber] = useState(false);
  const showNumber = !practice && (!!m.number || addNumber);
  // «Признать, что работа выполнена / и защищена с отметкой» - как writer _grade_break: первая часть - строкой во всю
  // ширину, вторая - рядом с полями отметки и даты (после «с» - пробел NB: неразрывный только при USE_NBSP)
  const gm = /^(.*?)\s+(и защищен.*)$/.exec(w.grade || '');
  const gradeHead = gm ? gm[1] : '';
  const gradeTail = gm ? gm[2].replace(' с ', ' с' + NB) : w.grade;
  // пустая левая часть строки - кеглем строки (подпись 8 пт - строка 9,2 пт, как в шаблоне)
  const right = (key: string, children: React.ReactNode, className = '', onClick?: (e: React.MouseEvent) => void) => [
    <div key={key + 'l'} className={'tv-c tv-l3' + (className.includes('tv-cap') ? ' tv-8' : '')} />,
    <div key={key} className={'tv-c tv-r4 ' + className} onClick={onClick}>{children}</div>,
  ];
  const cap = (key: string, text: string, cls: string) => <div key={key} className={'tv-c tv-cap ' + cls}>{text}</div>;
  return (
    <div className={'title-view tv-word' + (compact ? ' is-compact' : '')} data-testid="title-view">
      <div className="tv-p tv-10 ts-upper">Министерство науки и высшего образования Российской Федерации</div>
      <div className="tv-p tv-11">федеральное государственное автономное образовательное учреждение</div>
      <div className="tv-p tv-11">высшего образования</div>
      <div className="tv-p tv-11 ts-bold ts-click" onClick={open('student')}>{st.university || <span className="ts-empty">университет</span>}</div>
      <div className="tv-grid">
        <div className="tv-c tv-all ts-center ts-click" onClick={open('student')}>{st.branch || UI_SP}</div>
        <div className="tv-c tv-mid ts-center ts-bold tv-b ts-click" onClick={open('student')}>{st.institute || <span className="ts-empty">институт / высшая школа</span>}</div>
        {cap('c1', '(наименование высшей школы / филиала / института / колледжа)', 'tv-mid')}
        <div className="tv-all tv-sp600" />
        <div className="tv-c tv-all ts-center ts-bold tv-15 ts-worktype">
          {w.label}{w.sub ? <><br />{w.sub}</> : null}
          {showNumber && <> №{NB}{set ? <Inline value={m.number} onChange={(v) => { set({ number: v }); if (!v) setAddNumber(false); }} placeholder="…" className="ts-num" testid="ts-number" /> : m.number}</>}
          {set && !practice && !showNumber && (
            <button type="button" className="tv-add tv-add-num" onClick={(e) => { e.stopPropagation(); setAddNumber(true); }}
              title="Номер работы («№ 3») - если он есть" data-testid="ts-add-number">+ №</button>
          )}
        </div>
        <div className="tv-all tv-sp600" />
        {!practice && (
          <div className="tv-c tv-all tv-b ts-row"><span className="ts-lbl">По дисциплине</span>
            <Inline value={m.discipline} onChange={set && ((v) => set({ discipline: v }))} placeholder="укажите дисциплину" testid="ts-discipline" className="ts-grow" /></div>
        )}
        {practice && <>
          <div className="tv-c tv-all tv-b ts-click" onClick={open('practice')}>Вид практики: <span className={m.practice.kind ? '' : 'ts-empty'}>{m.practice.kind || '(учебная / производственная)'}</span></div>
          {cap('c2', '(учебная / производственная)', 'tv-all')}
          <div className="tv-c tv-all tv-b ts-click" onClick={open('practice')}>Тип практики: <span className={m.practice.type ? '' : 'ts-empty'}>{m.practice.type || ''}</span></div>
          <div className="tv-c tv-all tv-b ts-click" onClick={open('practice')}>Место прохождения практики (база практики): <span className={m.practice.place ? '' : 'ts-empty'}>{m.practice.place || ''}</span></div>
          {cap('c3', '(наименование места прохождения практики)', 'tv-all')}
          <div className="tv-c tv-all tv-b ts-click" onClick={open('practice')}>Срок прохождения:</div>
          <div className="tv-c tv-all tv-b ts-click" onClick={open('practice')}>с {m.practice.from || '«___» ________ 20__ г.'} по {m.practice.to || '«___» ________ 20__ г.'}</div>
        </>}
        <div className="tv-c tv-all tv-b ts-row tv-theme"><span className="ts-lbl">На тему</span>
          <Inline value={m.title} onChange={set && ((v) => set({ title: v }))} placeholder="укажите тему работы" testid="ts-title" className="ts-grow" multiline />
          {set && !practice && !showVariant && (
            <button type="button" className="tv-add" onClick={(e) => { e.stopPropagation(); setAddVariant(true); }}
              title="Строка «Вариант №» на титуле - только если вариант задан" data-testid="ts-add-variant">+ вариант</button>
          )}
        </div>
        {showVariant && <>
          <div className="tv-c tv-all tv-b">{UI_SP}</div>
          <div className="tv-c tv-all tv-b ts-center ts-variant" title="Пусто - строки «Вариант №» на титуле не будет">Вариант №{NB}
            <Inline value={m.variant} onChange={set && ((v) => { set({ variant: v }); if (!v) setAddVariant(false); })} placeholder={set ? '…' : ''} testid="ts-variant" className="ts-num" /></div>
        </>}
        <div className="tv-all tv-sp1120" />
        {right('r1', coauthors.length ? 'Выполнили обучающиеся:' : 'Выполнил обучающийся:')}
        {right('r2', st.student || <span className="ts-empty">Ф. И. О.</span>, 'tv-b ts-click', open('student'))}
        {coauthors.map((name, i) => right('r2co' + i, name, 'tv-b ts-click', open('student')))}
        {right('r3', '(Ф.И.О.)', 'tv-cap')}
        {right('r4', 'Направление подготовки / специальность:')}
        {right('r5', `${st.direction_code} «${st.direction_name}»`, 'tv-b ts-click', open('student'))}
        {right('r6', '(код и наименование)', 'tv-cap')}
        {right('r7', `Курс: ${st.course}`, 'tv-b ts-click', open('student'))}
        {right('r8', `Группа: ${st.group}`, 'tv-b ts-click', open('student'))}
        {right('r9', practice ? 'Руководитель практики от университета:' : 'Руководитель:')}
        {right('r10', <>
          <Inline value={t.name} onChange={set && ((v) => set({ teacher: { ...t, name: v } }))}
            placeholder={t.short ? `${t.short} (впишите Ф. И. О. полностью)` : 'Ф. И. О. руководителя'} testid="ts-teacher" />
          {t.position && <span>,{UI_SP}</span>}
          {(t.position || set) && <span className={t.position ? 'ts-pos' : 'ts-pos ts-optional'} title="Должность / степень / звание - только если точно известны">
            <Inline value={t.position} onChange={set && ((v) => set({ teacher: { ...t, position: v } }))} placeholder={set ? '+ должность' : ''} testid="ts-position" /></span>}
        </>, 'tv-b tv-teacher')}
        {right('r11', '(Ф.И.О. руководителя, должность / уч. степень / звание)', 'tv-cap')}
        <div className="tv-all tv-sp680" />
        {gradeHead && <div className="tv-c tv-all">{gradeHead}</div>}
        <div className="tv-c tv-g1">{gradeTail}</div><div className="tv-c tv-g2 tv-b" /><div className="tv-c tv-g3" /><div className="tv-c tv-g4 tv-b" />
        <div className="tv-c tv-g1 tv-8" />{cap('c4', '(отметка прописью)', 'tv-g2')}<div className="tv-c tv-g3 tv-8" />{cap('c5', '(дата)', 'tv-g4')}
        <div className="tv-c tv-g1">Руководитель</div><div className="tv-c tv-g2 tv-b" /><div className="tv-c tv-g3" />
        <div className="tv-c tv-g4 tv-b ts-center ts-short">{t.name ? shortName(t.name) : UI_SP}</div>
        <div className="tv-c tv-g1 tv-8" />{cap('c6', '(подпись руководителя)', 'tv-g2')}<div className="tv-c tv-g3 tv-8" />{cap('c7', '(инициалы, фамилия)', 'tv-g4')}
      </div>
      <div className="tv-sp3400" />
      <div className="tv-p tv-12 ts-center ts-bottom ts-click" onClick={open('student')}>{st.city} {st.year}</div>
    </div>
  );
}

/** Лист «Титульный лист» над текстом. */
export function TitleSheet({ ctx }: { ctx: Ctx }) {
  const m = useApp((s) => s.meta);
  if (!m) return null;
  const missing = [!m.title && 'тема', !m.discipline && m.type !== 'practice' && 'дисциплина', !m.teacher.name && 'руководитель'].filter(Boolean);
  return (
    <div className="title-sheet" data-testid="title-sheet">
      <div className="sheet-tools">
        {missing.length > 0 && <span className="sheet-miss"><Icon name="warn" size={13} /> Не заполнено: {missing.join(', ')}</span>}
        <span className="muted">Титул (Прил. Б) - поля правятся прямо на листе</span>
        <button type="button" className="btn-mini" onClick={() => ctx.openDialog('sto.meta', { tab: 'work' })} data-testid="title-edit">Данные работы…</button>
        <button type="button" className="btn-mini" onClick={() => ctx.openDialog('sto.titlePreview', {})} title="Собрать титул в Word и показать точно, как он будет напечатан"
          data-testid="title-exact">Точный вид (Word)</button>
      </div>
      <TitleView m={m} edit={{ set: (p) => ctx.store.getState().setMeta((mm) => ({ ...mm, ...p })) }}
        onOpen={(tab) => ctx.openDialog('sto.meta', { tab })} />
    </div>
  );
}

/** Точный предпросмотр: сервер собирает титул и служебные листы в Word и возвращает PNG страниц. */
export function TitlePreviewDialog({ ctx, props, close }: DialogProps) {
  const meta = (props.meta as Meta) || ctx.store.getState().meta!;
  const [pages, setPages] = useState<string[] | null>(null);
  const [err, setErr] = useState('');
  const [progress, setProgress] = useState('Запуск…');
  const n = 1 + (meta.zadanie?.enabled ? 1 : 0) + (meta.remarks_page ? 1 : 0);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { job } = await ctx.api.post<{ job: string }>('/api/sto/title-preview', { meta, pages: n });
        const r = await ctx.api.waitJob<{ pages: string[] }>(job, (s) => alive && setProgress(s.progress));
        if (alive) setPages(r.pages);
      } catch (e: any) { if (alive) setErr(e.message); }
    })();
    return () => { alive = false; };
  }, []);
  return (
    <Modal title="Титульный лист - точный вид (Word)" onClose={() => close()} width={Math.min(1240, 460 * n + 60)} testid="title-preview-dialog"
      footer={<Btn label="Закрыть" onClick={() => close()} />}>
      {!pages && !err && <div className="muted pad"><Icon name="spinner" className="spin" /> {progress} Word собирает титул (10–20 с)…</div>}
      {err && <div className="finding finding-E">{err}</div>}
      {pages && <div className="exact-pages" data-testid="title-preview-pages">{pages.map((p, i) => <img key={i} src={p} alt={'Страница ' + (i + 1)} />)}</div>}
    </Modal>
  );
}

/** Профиль для подстановки: подгружается один раз. */
export function useProfile() {
  const [p, setP] = useState(getProfileCached());
  useEffect(() => { void loadProfile().then(setP).catch(() => undefined); }, []);
  return p;
}
